import { useAccess, useChain, useEnvironmentSettings, useReorderChain } from "../../api/hooks";
import { Hint } from "../../components/Hint";
import { NotHeld } from "../../components/NotHeld";
import { Card, ErrorState, LoadingState, MutationError, StatusPill } from "../../components/ui";

/**
 * The organisation's chain and this environment's switches (ADR-0008),
 * unchanged in what they do and now behind `environment.chain:edit` and
 * `environment:edit` (ADR-0017): declared, never inferred from position.
 */
export function EnvironmentsSection({ environmentId }: { environmentId: string }) {
  const q = useChain(environmentId);
  const access = useAccess();
  const reorder = useReorderChain(environmentId);
  const settings = useEnvironmentSettings(environmentId);
  const canReorder = access.can(environmentId, "environment.chain:edit");
  const canEdit = access.can(environmentId, "environment:edit");
  if (q.isPending) return <LoadingState label="Loading the chain" />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  const chain = q.data.chain;

  const move = (index: number, by: -1 | 1) => {
    const ids = chain.map((c) => c.environmentId);
    const target = index + by;
    if (target < 0 || target >= ids.length) return;
    [ids[index], ids[target]] = [ids[target]!, ids[index]!];
    reorder.mutate({ environmentIds: ids });
  };

  return (
    <Card title={`${q.data.name}: the environment chain`}>
      <p className="mb-2 text-sm text-neutral-600 dark:text-neutral-400">
        Promotion runs upward. Whether an environment is live, and whether changes into it need approval, are declared
        here rather than inferred from where it sits - a sandbox may legitimately sit above production.
      </p>
      <MutationError of={[reorder, settings]} className="mb-3" />
      {!canReorder && <NotHeld permission="environment.chain:edit" className="mb-2 block" />}
      <ol aria-label="Environment chain, lowest first" className="space-y-2">
        {chain.map((c, i) => (
          <li key={c.environmentId} className="flex flex-wrap items-center gap-3 rounded border border-neutral-200 p-3 dark:border-neutral-800">
            <span className="text-xs text-neutral-500">{i + 1}</span>
            <span className="font-medium">{c.friendlyName}</span>
            {c.live && <StatusPill status="live" tone="critical" />}
            {c.changesRequireApproval && <StatusPill status="approval required" tone="warning" />}
            {c.breakGlassEnabled && <StatusPill status="break glass enabled" tone="neutral" />}
            <span className="ml-auto flex gap-2">
              <button type="button" className="text-sm underline disabled:opacity-40" disabled={!canReorder || i === 0 || reorder.isPending} onClick={() => move(i, -1)}>
                Move up
              </button>
              <button type="button" className="text-sm underline disabled:opacity-40" disabled={!canReorder || i === chain.length - 1 || reorder.isPending} onClick={() => move(i, 1)}>
                Move down
              </button>
            </span>
            {c.environmentId === environmentId && (
              <div className="flex w-full flex-wrap gap-4 border-t border-neutral-200 pt-2 text-sm dark:border-neutral-800">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={c.live} disabled={!canEdit || settings.isPending} onChange={(e) => settings.mutate({ live: e.target.checked })} />
                  Real callers reach this environment
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={c.changesRequireApproval} disabled={!canEdit || settings.isPending} onChange={(e) => settings.mutate({ changesRequireApproval: e.target.checked })} />
                  Changes need a manager's approval
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={c.breakGlassEnabled} disabled={!canEdit || settings.isPending} onChange={(e) => settings.mutate({ breakGlassEnabled: e.target.checked })} />
                  Allow break glass
                  <Hint id="access.break-glass" label="what break glass means" />
                </label>
                {!canEdit && <NotHeld permission="environment:edit" className="basis-full" />}
              </div>
            )}
          </li>
        ))}
      </ol>
    </Card>
  );
}
